import { makeAssistantToolUI } from '@assistant-ui/react'
import { useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  addContributionToActive,
  openAsExploration,
} from '@/components/agent/tools/_shared/applyContribution'
import type { ToolActionHandlers } from '@/components/agent/tools/_shared/chat-tool-actions'
import { MapCardActions } from '@/components/agent/tools/_shared/MapCardActions'
import { FindEntitiesNearLocationView } from '@/components/agent/tools/FindEntitiesNearLocationView'
import { getOrCreateInvestigationsStore } from '@/stores/investigations-store'
import { emptyViewState, type GraphView } from '@/types/graph-view'
import {
  extractEntityIds,
  type FindEntitiesNearLocationArgs,
  type FindEntitiesNearLocationResult,
  parseResult,
} from './find-entities-near-location'
import { useToolCardActions } from './useToolCardActions'

type Status = { type: 'running' | 'complete' | 'incomplete' | string }

interface FindEntitiesNearLocationRenderProps {
  args: FindEntitiesNearLocationArgs
  result?: FindEntitiesNearLocationResult | string
  status: Status
}

/**
 * Connected `find_entities_near_location` tool card: parses the backend
 * payload, builds a GraphView from the anchors + their connected entities,
 * resolves the map action footer from the chat context, and hands pure data
 * to FindEntitiesNearLocationView.
 */
function FindEntitiesNearLocationRender({
  args,
  result,
  status,
}: FindEntitiesNearLocationRenderProps) {
  const navigate = useNavigate()
  const { caseId } = useParams<{ caseId: string }>()

  const running = status.type === 'running'
  const parsed = useMemo(() => parseResult(result), [result])
  const errored =
    parsed?.status === 'error' || parsed?.status === 'disabled' || status.type === 'incomplete'
  const entityIds = useMemo(() => extractEntityIds(parsed), [parsed])

  const locationName = parsed?.location?.name ?? args.location

  // Build a GraphView from the anchors + their connected entities so the
  // standard tool actions can merge them into the investigation object set —
  // same contract as the other graph tool cards. Only anchors carry
  // coordinates (`_geo_lat`/`_geo_lon`, read by useInvestigationGeoData), so
  // only they plot as pins; neighbours are reached by expanding the anchor.
  const handlers = useMemo<ToolActionHandlers>(() => {
    if (parsed?.status !== 'ok' || !parsed.location || !caseId) return {}
    const anchors = parsed.anchors ?? []
    const label = `Near ${locationName}`
    const anchorNodes = anchors.map(a => ({
      id: a.anchor_id,
      labels: [a.type],
      typeLabel: a.type,
      name: a.name,
      properties: { _geo_lat: a.latitude, _geo_lon: a.longitude, name: a.name },
    }))
    const neighborNodes = anchors.flatMap(a =>
      (a.neighbors ?? []).map(n => ({
        id: n.id,
        labels: [n.type],
        typeLabel: n.type,
        name: n.name,
        properties: { name: n.name },
      }))
    )
    // Edges point from the connected subject to its anchor (the via_edge
    // direction), so the graph view renders neighbour-expansion off the pin.
    const edges = anchors.flatMap(a =>
      (a.neighbors ?? []).map(n => ({
        id: `${n.id}->${a.anchor_id}`,
        type: 'NEAR_ANCHOR',
        source: n.id,
        target: a.anchor_id,
        properties: {},
      }))
    )
    const view: GraphView = {
      meta: { source: 'assistant', createdAt: new Date().toISOString(), caseId, label },
      nodes: [...anchorNodes, ...neighborNodes],
      edges,
      view: emptyViewState(),
    }

    // The map auto-fits to the active investigation's object set, so merging
    // the entities + navigating to /geolocation is all that's needed.
    const goToMap = () => navigate(`/cases/${caseId}/geolocation`)

    const explore = () => {
      const store = getOrCreateInvestigationsStore({ caseId })
      openAsExploration({ store, view, label })
      goToMap()
    }

    return {
      showOnGraph: explore,
      showSeparately: explore,
      addToGraph: () => {
        const store = getOrCreateInvestigationsStore({ caseId })
        addContributionToActive({ store, view, label })
        goToMap()
      },
      // Entities already in the object set — don't re-merge, just open the map.
      highlight: goToMap,
    }
  }, [parsed, caseId, locationName, navigate])

  const actionsModel = useToolCardActions(entityIds, handlers)
  const actions = actionsModel ? <MapCardActions model={actionsModel} handlers={handlers} /> : null

  return (
    <FindEntitiesNearLocationView
      args={args}
      parsed={parsed}
      running={running}
      errored={errored}
      actions={actions}
    />
  )
}

export const FindEntitiesNearLocationToolUI = makeAssistantToolUI<
  FindEntitiesNearLocationArgs,
  FindEntitiesNearLocationResult | string
>({
  toolName: 'find_entities_near_location',
  render: FindEntitiesNearLocationRender,
})
